<template>
  <InfuseSlideInLayout
    :title="role?.name"
    :subtitle="role?.description"
    :parent-to="parentTo"
    :editable="true"
    @edit="showUpdateRoleModal = true"
  >
    <AssetLevelOverview v-if="role" :asset="role" />

    <InfuseRouterTabLayout v-if="role" :model="role" :is-loading="isLoading" />

    <!-- UPDATE -->
    <MutationRoleModal
      v-if="showUpdateRoleModal"
      :role="role"
      :loading="isUpdatingRole"
      @close="closeUpdateModal"
      @mutate="onUpdateRole"
    />
  </InfuseSlideInLayout>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';

import { AssetLevelOverview } from '@infuse/iam/components';
import { useRouteLevel } from '@infuse/iam/composables';
import { useUpdateRole } from '@infuse/iam/role/mutations';
import { useRole } from '@infuse/iam/role/queries';
import { UpdateRole } from '@infuse/iam/role/type';
import { MutationRoleModal } from '@infuse/iam/role/ui';
import { UserLevel } from '@infuse/iam/user/type';
import { InfuseRouterTabLayout, InfuseSlideInLayout } from '@infuse/ui/layouts';
import { permissionOrRedirect } from '@infuse/ui/utils';
import { computed, ref, watch } from 'vue';

const route = useRoute();

const { role, isLoading, fetchRoleId, levelType, levelId } = useRole();

const { level } = useRouteLevel();

const parentTo = computed(() => {
  if (level.value === UserLevel.TENANT) {
    return { name: 'tenant.roles' };
  } else if (level.value === UserLevel.ORGANISATION) {
    return { name: 'organisation.roles' };
  }

  return { name: 'role' };
});

const showUpdateRoleModal = ref(false);

const { mutateAsync: updateRole, isPending: isUpdatingRole } = useUpdateRole();

permissionOrRedirect();

watch(
  () => [route.name, route.params.roleId, route.params.id, route.params.level, route.params.levelId],
  () => {
    let routeLevel = undefined;
    let routeLevelId = undefined;
    if (route.name?.toString().startsWith('organisation')) {
      routeLevel = 'organisation';
      routeLevelId = route.params.id as string;
    } else if (route.name?.toString().startsWith('tenant')) {
      routeLevel = 'tenant';
      routeLevelId = route.params.id as string;
    } else if (route.params.level) {
      routeLevel = route.params.level as string;
      routeLevelId = route.params.levelId as string;
    }

    levelType.value = routeLevel;
    levelId.value = routeLevelId;
    fetchRoleId.value = `${route.params.roleId || route.params.id}`;
  },
  { immediate: true }
);

async function onUpdateRole(vars: { id: string; role: UpdateRole }): Promise<void> {
  await updateRole({ roleId: vars.id, role: vars.role });
  closeUpdateModal();
}

function closeUpdateModal() {
  showUpdateRoleModal.value = false;
}
</script>
